'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { DotLottiePlayer } from '@dotlottie/react-player';

interface ContactHeroProps {
    className?: string;
}

const ContactHero: React.FC<ContactHeroProps> = ({ className = '' }) => {
    return (
        <section className={`bg-white w-full relative overflow-hidden min-h-[80vh] flex items-center pt-28 ${className}`}>

            {/* Background Grid Pattern - "Free Element" */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
                style={{
                    backgroundImage: 'radial-gradient(#4f46e5 1px, transparent 1px)',
                    backgroundSize: '24px 24px'
                }}>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full relative z-10 h-full">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center h-full">

                    {/* Left: Typography Content */}
                    <div className="text-left order-2 lg:order-1 pt-10 lg:pt-0 pb-12 lg:pb-0">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-slate-900 leading-[0.9] mb-8">
                                LET'S BUILD<br />
                                <span className="text-indigo-600">SOMETHING</span><br />
                                AMAZING.
                            </h1>
                            <p className="text-xl text-slate-500 max-w-lg leading-relaxed mb-10 font-medium">
                                Transform your digital presence with our comprehensive clarity-first solutions.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
                                <div className="flex flex-wrap gap-4">
                                    <span className="inline-flex items-center px-4 py-2 rounded-full bg-slate-100 border border-slate-200 text-xs font-bold text-slate-700 uppercase tracking-wider shadow-sm">
                                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                                        Available Now
                                    </span>
                                    <span className="inline-flex items-center px-4 py-2 rounded-full bg-slate-100 border border-slate-200 text-xs font-bold text-slate-700 uppercase tracking-wider shadow-sm">
                                        <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                                        24-48h Response
                                    </span>
                                </div>

                                {/* Trust Indicator - "Free Element" */}
                                <div className="flex items-center gap-3 pl-0 sm:pl-4 sm:border-l border-slate-200">
                                    <div className="flex -space-x-2">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="w-8 h-8 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center overflow-hidden">
                                                <div className="w-full h-full bg-gradient-to-br from-slate-300 to-slate-400"></div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="text-xs font-medium text-slate-500">
                                        Trusted by <span className="text-slate-900 font-bold">50+</span> Brands
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right: Lottie Animation with Visual Enhancements */}
                    <div className="relative order-1 lg:order-2 flex justify-center lg:justify-end items-center h-full">
                        {/* Ambient Glow - "Free Element" */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-indigo-500/10 blur-[100px] rounded-full pointer-events-none"></div>

                        {/* Floating Element 1: Support Team (Top Left) */}
                        <motion.div
                            animate={{ y: [0, -10, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -top-4 md:top-4 left-4 lg:-left-12 z-20 bg-white p-3 rounded-2xl shadow-xl shadow-indigo-100 border border-slate-100 flex items-center gap-3"
                        >
                            <div className="flex -space-x-2">
                                <div className="w-8 h-8 rounded-full bg-blue-100 border-2 border-white"></div>
                                <div className="w-8 h-8 rounded-full bg-indigo-100 border-2 border-white"></div>
                                <div className="w-8 h-8 rounded-full bg-green-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-green-700">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                </div>
                            </div>
                            <div>
                                <div className="text-xs font-bold text-slate-800">Support Team</div>
                                <div className="text-[10px] text-green-600 font-medium">Online Now</div>
                            </div>
                        </motion.div>

                        {/* Floating Element 2: Success Badge (Bottom Right) */}
                        <motion.div
                            animate={{ y: [0, 15, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                            className="absolute bottom-4 md:bottom-12 right-4 lg:-right-4 z-20 bg-white px-4 py-3 rounded-xl shadow-xl shadow-indigo-100 border border-slate-100 flex items-center gap-3"
                        >
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <div className="text-xs font-bold text-slate-800">Message Sent</div>
                                <div className="text-[10px] text-slate-500">We'll reply shortly</div>
                            </div>
                        </motion.div>

                        {/* Floating Element 3: 5-Star Rating (Top Right) */}
                        <motion.div
                            animate={{ y: [0, -12, 0] }}
                            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                            className="absolute top-2 md:top-10 right-8 lg:-right-8 z-20 bg-white px-3 py-2 rounded-xl shadow-lg shadow-yellow-100/50 border border-slate-50 flex items-center gap-2"
                        >
                            <div className="flex text-yellow-400">
                                {[1, 2, 3, 4, 5].map(i => (
                                    <svg key={i} className="w-3 h-3 fill-current" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                ))}
                            </div>
                            <span className="text-xs font-bold text-slate-800">5.0</span>
                        </motion.div>

                        {/* Floating Element 4: Scheduled (Bottom Left) */}
                        <motion.div
                            animate={{ y: [0, 8, 0] }}
                            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                            className="absolute bottom-4 md:bottom-20 left-0 lg:-left-8 z-20 bg-white/90 backdrop-blur px-4 py-3 rounded-xl shadow-xl shadow-indigo-100/50 border border-slate-100 flex items-center gap-3"
                        >
                            <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex flex-col items-center justify-center">
                                <span className="text-[6px] font-bold text-indigo-400 uppercase">DEC</span>
                                <span className="text-xs font-bold text-indigo-700 leading-none">24</span>
                            </div>
                            <div>
                                <div className="text-xs font-bold text-slate-800">Consultation</div>
                                <div className="text-[10px] text-slate-500">10:00 AM • Scheduled</div>
                            </div>
                        </motion.div>

                        {/* Floating Element 5: Growth Graph (Middle Right - Moved Further Right) */}
                        <motion.div
                            animate={{ y: [0, -15, 0] }}
                            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                            className="absolute top-[50%] right-0 lg:-right-48 z-20 bg-white px-3 py-3 rounded-xl shadow-xl shadow-indigo-100/50 border border-slate-100 hidden sm:block"
                        >
                            <div className="flex items-center gap-3 mb-1">
                                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="text-[10px] text-slate-500 font-medium">Efficiency</div>
                                    <div className="text-xs font-bold text-slate-900">+24%</div>
                                </div>
                            </div>
                            <div className="h-6 w-24 flex items-end gap-1">
                                <div className="w-1/5 bg-indigo-100 rounded-t-sm h-[40%]"></div>
                                <div className="w-1/5 bg-indigo-200 rounded-t-sm h-[60%]"></div>
                                <div className="w-1/5 bg-indigo-300 rounded-t-sm h-[50%]"></div>
                                <div className="w-1/5 bg-indigo-400 rounded-t-sm h-[80%]"></div>
                                <div className="w-1/5 bg-indigo-600 rounded-t-sm h-[100%]"></div>
                            </div>
                        </motion.div>

                        {/* Floating Element 6: Verified Badge (Top Far Right) */}
                        <motion.div
                            animate={{ y: [0, 10, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 3 }}
                            className="absolute top-12 right-0 lg:-right-24 z-20 bg-white p-2 rounded-full shadow-lg shadow-indigo-100 border border-slate-50 hidden sm:block"
                        >
                            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-indigo-600">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                        </motion.div>

                        {/* Floating Element 7: Secure Data (Top Left - Lower) */}
                        <motion.div
                            animate={{ y: [0, -8, 0] }}
                            transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                            className="absolute top-20 -left-6 lg:-left-28 z-20 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full shadow-lg shadow-indigo-50 border border-slate-50 hidden sm:flex items-center gap-2"
                        >
                            <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></div>
                            <span className="text-[10px] font-bold text-slate-600">Secure Data</span>
                        </motion.div>

                        {/* Floating Element 8: Global Reach (Bottom Center Right) */}
                        <motion.div
                            animate={{ y: [0, 12, 0] }}
                            transition={{ duration: 5.2, repeat: Infinity, ease: "easeInOut", delay: 2.5 }}
                            className="absolute bottom-10 right-12 lg:-right-24 z-20 bg-white p-2.5 rounded-xl shadow-lg shadow-blue-100 border border-slate-50 hidden sm:block"
                        >
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                            className="w-full max-w-[700px] lg:-mr-20 relative z-10"
                        >
                            <DotLottiePlayer
                                src="/animations/contact-hero.lottie"
                                autoplay
                                loop
                                className="w-full h-full object-contain drop-shadow-2xl"
                            />
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactHero;